import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useCallback } from 'react'
import { questionsApi } from '../utils/api'
import { useAppStore } from '../stores/appStore'
import { formatTimeAgo } from '../utils/format'
import { QuestionCard } from './QuestionCard'
import { QuestionListSkeleton } from './Skeleton'
import clsx from 'clsx'

// Quick filter presets
type QuickFilter = 'all' | 'high_edge' | 'closing_soon' | 'high_liquidity' | 'never_predicted'

interface QuickFilterConfig {
  key: QuickFilter
  label: string
  icon: string
  description: string
}

const QUICK_FILTERS: QuickFilterConfig[] = [
  { key: 'all', label: 'All', icon: '📋', description: 'Show all questions' },
  { key: 'high_edge', label: 'High Edge', icon: '🎯', description: 'Edge > 5%' },
  { key: 'closing_soon', label: 'Closing <24h', icon: '⏰', description: 'Markets closing within 24 hours' },
  { key: 'high_liquidity', label: 'High TVL', icon: '💰', description: 'Liquidity > $50k' },
  { key: 'never_predicted', label: 'New', icon: '✨', description: 'Never been predicted' },
]

// Valid quick filter values for validation
const VALID_QUICK_FILTERS = new Set<QuickFilter>(['all', 'high_edge', 'closing_soon', 'high_liquidity', 'never_predicted'])
const VALID_SORTS = new Set(['recent', 'liquidity', 'volume', 'open_interest', 'edge', 'confidence', 'close'])

export function QuestionList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { filters, setFilters, openRunner } = useAppStore()

  // Sync URL params to store on mount and URL changes
  useEffect(() => {
    const urlCategory = searchParams.get('category')
    const urlSearch = searchParams.get('q')
    const urlSort = searchParams.get('sort')
    const urlQuickFilter = searchParams.get('filter') as QuickFilter | null
    const urlHasPosition = searchParams.get('position')

    const updates: Partial<typeof filters> = {}

    if (urlCategory !== null && urlCategory !== filters.category) {
      updates.category = urlCategory || null
    }
    if (urlSearch !== null && urlSearch !== filters.search) {
      updates.search = urlSearch
    }
    if (urlSort && VALID_SORTS.has(urlSort) && urlSort !== filters.sort) {
      updates.sort = urlSort
    }
    if (urlQuickFilter && VALID_QUICK_FILTERS.has(urlQuickFilter) && urlQuickFilter !== filters.quickFilter) {
      updates.quickFilter = urlQuickFilter
    }
    if (urlHasPosition !== null) {
      const hasPos = urlHasPosition === 'true' ? true : urlHasPosition === 'false' ? false : null
      if (hasPos !== filters.hasPosition) {
        updates.hasPosition = hasPos
      }
    }

    if (Object.keys(updates).length > 0) {
      setFilters(updates)
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // Update URL when filters change
  const updateURLParams = useCallback((newFilters: Partial<typeof filters>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      if (newFilters.category !== undefined) {
        if (newFilters.category) {
          next.set('category', newFilters.category)
        } else {
          next.delete('category')
        }
      }
      if (newFilters.search !== undefined) {
        if (newFilters.search) {
          next.set('q', newFilters.search)
        } else {
          next.delete('q')
        }
      }
      if (newFilters.sort !== undefined && newFilters.sort !== 'recent') {
        next.set('sort', newFilters.sort)
      } else if (newFilters.sort === 'recent') {
        next.delete('sort')
      }
      if (newFilters.quickFilter !== undefined && newFilters.quickFilter !== 'all') {
        next.set('filter', newFilters.quickFilter)
      } else if (newFilters.quickFilter === 'all') {
        next.delete('filter')
      }
      if (newFilters.hasPosition !== undefined) {
        if (newFilters.hasPosition === true) {
          next.set('position', 'true')
        } else if (newFilters.hasPosition === false) {
          next.set('position', 'false')
        } else {
          next.delete('position')
        }
      }

      return next
    }, { replace: true })
  }, [setSearchParams])

  // Wrap setFilters to also update URL
  const handleSetFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters(newFilters)
    updateURLParams(newFilters)
  }, [setFilters, updateURLParams])

  // Quick filter state (in addition to regular filters)
  const activeQuickFilter: QuickFilter = filters.quickFilter || 'all'

  const setQuickFilter = (qf: QuickFilter) => {
    handleSetFilters({ quickFilter: qf })
  }

  // Build query params including quick filter mappings
  const buildQueryParams = () => {
    const params: Parameters<typeof questionsApi.list>[0] = {
      page: 1,
      page_size: 50,
      category: filters.category,
      status: filters.status,
      has_run: filters.hasRun,
      has_position: filters.hasPosition,
      search: filters.search || undefined,
      sort: filters.sort,
    }

    // Apply quick filter presets
    switch (activeQuickFilter) {
      case 'high_edge':
        params.sort = 'edge'
        break
      case 'closing_soon':
        params.sort = 'close'
        break
      case 'high_liquidity':
        params.sort = 'liquidity'
        break
      case 'never_predicted':
        params.has_run = false
        break
    }

    return params
  }

  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['questions', filters, activeQuickFilter],
    queryFn: () => questionsApi.list(buildQueryParams()),
    refetchInterval: 30000, // Refresh every 30s
  })
  
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: questionsApi.categories,
  })
  
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Page header */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
          Market Questions
        </h2>
        <p className="text-star-muted text-xs sm:text-sm">
          {data?.total ?? 0} questions tracked • Updated{' '}
          {dataUpdatedAt ? formatTimeAgo(new Date(dataUpdatedAt).toISOString()) : '—'}
          {isFetching && data ? <span className="text-star-dim"> • Refreshing…</span> : null}
        </p>
      </div>
      
      {/* Quick Filter Chips */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto pb-1">
        {QUICK_FILTERS.map((qf) => (
          <button
            key={qf.key}
            onClick={() => setQuickFilter(qf.key)}
            title={qf.description}
            className={clsx(
              'px-3 py-2 sm:py-1.5 rounded-full font-mono text-xs transition-all flex items-center gap-1.5 min-h-[40px] sm:min-h-0 whitespace-nowrap',
              activeQuickFilter === qf.key
                ? 'bg-signal-blue/20 text-signal-blue border border-signal-blue/40 shadow-glow-sm'
                : 'bg-void-100 text-star-muted border border-star-dim/30 hover:bg-void-200 hover:text-star'
            )}
          >
            <span>{qf.icon}</span>
            <span className="hidden xs:inline">{qf.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="glass-card p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <input
              type="text"
              placeholder="Search questions..."
              className="input min-h-[44px]"
              value={filters.search}
              onChange={(e) => handleSetFilters({ search: e.target.value })}
              aria-label="Search questions"
            />
          </div>

          {/* Mobile: Category + Sort in row */}
          <div className="flex gap-2 sm:contents">
            {/* Category filter */}
            <select
              className="input flex-1 sm:flex-none sm:w-auto min-h-[44px]"
              value={filters.category || ''}
              onChange={(e) => handleSetFilters({ category: e.target.value || null })}
              aria-label="Filter by category"
            >
              <option value="">All Categories</option>
              {categories?.categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              className="input flex-1 sm:flex-none sm:w-auto min-h-[44px]"
              value={filters.sort}
              onChange={(e) => handleSetFilters({ sort: e.target.value })}
              aria-label="Sort questions"
            >
              <option value="recent">Most Recent</option>
              <option value="liquidity">Highest TVL</option>
              <option value="volume">Highest Volume</option>
              <option value="open_interest">Open Interest</option>
              <option value="edge">Highest Edge</option>
              <option value="confidence">Highest Confidence</option>
              <option value="close">Closing Soon</option>
            </select>
          </div>

          {/* Mobile: Position filter + Refresh in row */}
          <div className="flex gap-2 sm:contents">
            {/* Position filter - cycles through: all -> has position -> no position -> all */}
            <button
              className={clsx(
                'flex-1 sm:flex-none px-3 py-2 rounded-lg font-mono text-xs transition-all min-h-[44px]',
                filters.hasPosition === true
                  ? 'bg-signal-purple/10 text-signal-purple border border-signal-purple/30'
                  : filters.hasPosition === false
                  ? 'bg-signal-orange/10 text-signal-orange border border-signal-orange/30'
                  : 'bg-void-100 text-star-muted border border-star-dim/30 hover:bg-void-200'
              )}
              aria-label="Filter by position status"
              onClick={() => handleSetFilters({
                hasPosition: filters.hasPosition === null ? true : filters.hasPosition === true ? false : null
              })}
            >
              {filters.hasPosition === true ? '◈ Has Position' : filters.hasPosition === false ? '◇ No Position' : '○ All Positions'}
            </button>

            {/* Refresh */}
            <button
              className="btn-secondary min-h-[44px]"
              onClick={() => refetch()}
              aria-label="Refresh questions list"
            >
              <span aria-hidden="true">↻</span> Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {isLoading && <QuestionListSkeleton />}
      
      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load questions</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}
      
      {/* Question grid */}
      {data && (
        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {data.items.map((question, index) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.03 }}
              >
                <QuestionCard
                  question={question}
                  onRunPrediction={() => openRunner(question.id, question.title)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      
      {/* Empty state */}
      {data && data.items.length === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-star-muted font-mono text-lg mb-2">No questions found</p>
          <p className="text-star-dim text-sm">Try adjusting your filters</p>
        </div>
      )}
      
      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="text-star-muted font-mono text-sm">
            Page {data.page} of {data.total_pages}
          </span>
        </div>
      )}
    </div>
  )
}

